import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import ProofSection from "@/components/landing/ProofSection";
import FreeVsPaid from "@/components/landing/FreeVsPaid";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  ShieldAlert, DraftingCompass, MousePointerClick, BookOpen, FileText,
  ArrowRight, Flame, CircuitBoard, ClipboardCheck, Layers, BadgeCheck,
} from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1721244654394-36a7bc2da288?crop=entropy&cs=srgb&fm=webp&w=1600&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwzfHxhcmNoaXRlY3R1cmFsJTIwYmx1ZXByaW50JTIwbGF5b3V0fGVufDB8fHx8MTc4NjkxMDMyOXww&ixlib=rb-4.1.0&q=70";
const ENGINEER_IMG = "https://images.unsplash.com/photo-1659353588681-a3584fce131d?crop=entropy&cs=srgb&fm=webp&w=1200&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHw0fHxlbmdpbmVlciUyMHRhYmxldCUyMGNvbnN0cnVjdGlvbiUyMGJsdWVwcmludHxlbnwwfHx8fDE3ODY5MTAzMjl8MA&ixlib=rb-4.1.0&q=75";

const FEATURE_ICONS = [MousePointerClick, DraftingCompass, BookOpen, FileText];

const WEBSITE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Fire Alarm Blueprint Trainer",
  url: "https://firealarmbt.com/",
  description: "Interactive fire alarm training and fire alarm system design practice.",
};

export default function Landing() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const startFree = () => navigate(user ? "/dashboard" : "/register");
  const features = [0, 1, 2, 3].map((i) => ({
    icon: FEATURE_ICONS[i],
    title: t(`landing.features.f${i}Title`),
    text: t(`landing.features.f${i}Text`),
  }));
  return (
    <div className="App flex flex-col min-h-screen bg-background">
      <Seo
        title="Learn Fire Alarm System Design With Interactive Blueprints"
        description="Practice fire alarm system design on interactive blueprints. Place NFPA 72 devices, get plain-language guidance, and train as a fire alarm technician, designer, or student — free educational trainer."
        path="/"
        jsonLd={WEBSITE_JSONLD}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt={t("landing.heroAlt")} width="1600" height="900" fetchPriority="high" decoding="async" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A122A] via-[#0A122A]/90 to-[#0A122A]/40" />
          <div className="absolute inset-0 grain-overlay opacity-[0.15]" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 py-14 sm:py-20 lg:py-28">
          <div className="max-w-3xl animate-fade-up">
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-primary border border-primary/40 rounded-full px-3 py-1 mb-6">
              <Flame size={14} /> {t("landing.badge")}
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-none mb-6" data-testid="hero-title">
              {t("landing.title")}
            </h1>
            <p className="text-base sm:text-lg text-slate-300 max-w-2xl leading-relaxed mb-8" data-testid="hero-subtitle">
              {t("landing.subtitle")}
            </p>
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <Button data-testid="cta-start-free" onClick={startFree}
                className="w-full sm:w-auto justify-center bg-primary hover:bg-primary/90 text-white rounded-full h-12 px-7 text-base">
                {t("landing.startFree")} <ArrowRight size={18} className="ml-1.5" />
              </Button>
              <button type="button" data-testid="cta-learn" onClick={() => navigate("/learn")}
                className="text-sm text-slate-300 hover:text-white underline-offset-4 hover:underline text-center sm:text-left py-2">
                {t("landing.ctaLearn")}
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-4" data-testid="hero-note">{t("landing.heroNote")}</p>
          </div>
        </div>
      </section>

      <ProofSection />
      <FreeVsPaid onStart={startFree} />

      {/* Disclaimer banner */}
      <div className="bg-yellow-500/10 border-b border-yellow-500/30">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-4 flex items-start gap-3">
          <ShieldAlert size={20} className="text-yellow-400 shrink-0 mt-0.5" />
          <p className="text-sm text-slate-200" data-testid="landing-disclaimer">
            <strong className="text-yellow-300">{t("landing.disclaimerStrong")}</strong> {t("landing.disclaimerBody")}
          </p>
        </div>
      </div>

      {/* Features */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 py-20 w-full">
        <div className="mb-12 max-w-2xl">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary">{t("landing.featuresEyebrow")}</span>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">{t("landing.featuresTitle")}</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 stagger-in">
          {features.map((f, i) => (
            <div key={f.title} data-testid={`feature-card-${i}`}
              className="group card-lift rounded-md border border-white/10 bg-card p-6 hover:border-primary/40">
              <span className="grid place-items-center w-11 h-11 rounded-md bg-primary/15 text-primary mb-4 group-hover:bg-primary group-hover:text-white transition-colors duration-200">
                <f.icon size={22} />
              </span>
              <h3 className="font-heading font-bold text-lg mb-2">{f.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Split showcase */}
      <section className="border-y border-white/10 bg-[#0B132B]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary">{t("landing.showcaseEyebrow")}</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2 mb-4">
              {t("landing.showcaseTitle")}
            </h2>
            <p className="text-slate-300 leading-relaxed mb-6">
              {t("landing.showcaseBody")}
            </p>
            <div className="space-y-3">
              {[
                { icon: Layers, t: t("landing.showcasePoints.p0") },
                { icon: CircuitBoard, t: t("landing.showcasePoints.p1") },
                { icon: ClipboardCheck, t: t("landing.showcasePoints.p2") },
              ].map((x) => (
                <div key={x.t} className="flex items-center gap-3">
                  <span className="grid place-items-center w-9 h-9 rounded-md bg-primary/15 text-primary shrink-0"><x.icon size={18} /></span>
                  <span className="text-sm text-slate-300">{x.t}</span>
                </div>
              ))}
            </div>
            <Button data-testid="showcase-cta" onClick={() => navigate("/workspace")}
              className="mt-8 bg-primary hover:bg-primary/90 text-white rounded-full h-11 px-6">
              {t("landing.showcaseCta")} <ArrowRight size={18} className="ml-1.5" />
            </Button>
          </div>
          <div className="relative rounded-lg overflow-hidden border border-white/10">
            <img src={ENGINEER_IMG} alt={t("landing.showcaseAlt")} width="1200" height="800" loading="lazy" decoding="async" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A122A] to-transparent opacity-60" />
          </div>
        </div>
      </section>

      {/* Certification Test & Scheduling */}
      <section className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-16">
          <div className="rounded-lg border border-primary/30 bg-gradient-to-br from-primary/15 to-transparent p-8 sm:p-12 relative overflow-hidden">
            <div className="absolute inset-0 grain-overlay opacity-[0.12]" />
            <div className="relative max-w-2xl">
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-primary mb-4">
                <BadgeCheck size={14} /> {t("landing.certEyebrow")}
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-4">{t("landing.certTitle")}</h2>
              <p className="text-base text-slate-300 leading-relaxed mb-8">{t("landing.certIntro")}</p>
              <Button data-testid="cta-schedule-test" onClick={() => navigate("/certification")}
                className="bg-primary hover:bg-primary/90 text-white rounded-full h-12 px-8 text-base font-semibold shadow-lg shadow-primary/30">
                {t("landing.certCta")} <ArrowRight size={18} className="ml-1.5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
